// src/services/api.js
import axios from 'axios';
import { debugLog } from '../utils/debugLogger';
import { rememberQuickExpensePath } from './nativeExpenseService';

const normalizeApiUrl = (rawUrl) => {
  const clean = (rawUrl || '').toString().trim().replace(/\/+$/, '');
  if (!clean) return '';
  return /\/api$/i.test(clean) ? clean : `${clean}/api`;
};

const resolveApiUrl = () => {
  const envApiUrl = normalizeApiUrl(process.env.REACT_APP_API_URL);
  if (envApiUrl) return envApiUrl;

  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const capacitor = window.Capacitor;
  const isNativeApp = typeof capacitor?.isNativePlatform === 'function'
    ? capacitor.isNativePlatform()
    : (typeof capacitor?.getPlatform === 'function' && capacitor.getPlatform() !== 'web');

  // En APK (Capacitor), localhost apunta al dispositivo, no al backend.
  if (isLocalhost && !isNativeApp) {
    return 'http://localhost:5000/api';
  }

  return 'https://gestion.smtlinkss.com/api';
};

const API_URL = resolveApiUrl();

// Crear instancia de axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar el token JWT a cada petición
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // ✅ Si es FormData (archivos), eliminar Content-Type para que axios lo configure automáticamente
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Logs para debugging (activar con REACT_APP_DEBUG_LOGS=true)
    debugLog('📤 Request:', config.method?.toUpperCase(), config.url);
    debugLog('🔑 Token:', token ? 'Presente' : '❌ Ausente');
    debugLog('📦 Data type:', config.data instanceof FormData ? 'FormData' : 'JSON');

    return config;
  },
  (error) => {
    console.error('❌ Error en request:', error);
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de respuesta
api.interceptors.response.use(
  (response) => {
    debugLog('✅ Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('❌ Error en response:', {
      status: error.response?.status,
      url: error.config?.url,
      message: error.response?.data?.message || error.message
    });

    // Solo 401 debe cerrar sesión automáticamente.
    // 403 = sin permisos, pero la sesión sigue siendo válida.
    const isAuthenticationFlow = String(error.config?.url || '').startsWith('/auth/login')
      || String(error.config?.url || '').startsWith('/auth/mfa/');
    if (error.response?.status === 401 && !isAuthenticationFlow) {
      if (window.location.pathname === '/expense-tickets/quick') {
        rememberQuickExpensePath(`/expense-tickets/quick${new URLSearchParams(window.location.search).get('capture') === '1' ? '?capture=1' : ''}`);
      }
      console.error('🚫 Token inválido o expirado (401)');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
